import { withAuthenticationRequired } from "@auth0/auth0-react";
import { store } from "../store";
import { useTranslation } from "react-i18next";
import {
  Grid,
  Paper,
  Typography,
  Box,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Tv,
  PowerSettingsNew,
  PowerOff,
  Business,
  FilterList,
  Inventory2,
} from "@mui/icons-material";
import ScreensTable from "../components/tables/Screens";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

function Screens(props) {
  const { t } = useTranslation("common");
  const screens = useSelector((state) => state.screens);
  const residences = useSelector((state) => state.residences);
  const establishments = useSelector((state) => state.establishments);

  const [selectedEstablishment, setSelectedEstablishment] = useState("all");

  // Fonction pour obtenir l'établissement d'un écran (via pack_id → residence → establishment)
  const getScreenEstablishment = (screen) => {
    const residence = residences.find((r) => r.pack_id === screen.pack_id);
    if (!residence) return null;
    return establishments.find(
      (e) => parseInt(e.id) === parseInt(residence.establishment_id)
    );
  };

  // Filtrer les écrans selon l'établissement sélectionné
  const filteredScreens =
    selectedEstablishment === "all"
      ? screens
      : selectedEstablishment === "unassigned"
        ? screens.filter((screen) => !getScreenEstablishment(screen))
        : screens.filter((screen) => {
            const estab = getScreenEstablishment(screen);
            return estab && parseInt(estab.id) === parseInt(selectedEstablishment);
          });

  // Calculer les statistiques
  const totalScreens = filteredScreens.length;
  const screensOn = filteredScreens.filter((s) => s.screen_on).length;
  const screensOff = totalScreens - screensOn;

  const statsCards = [
    {
      title: t("common.total"),
      value: totalScreens,
      icon: Tv,
      color: "#78a882",
      bgColor: alpha("#78a882", 0.08),
    },
    {
      title: t("common.screen_on") || "Allumés",
      value: screensOn,
      icon: PowerSettingsNew,
      color: "#6b9dad",
      bgColor: alpha("#6b9dad", 0.08),
    },
    {
      title: t("common.screen_off") || "Éteints",
      value: screensOff,
      icon: PowerOff,
      color: "#7a8a7a",
      bgColor: alpha("#7a8a7a", 0.08),
    },
  ];

  useEffect(() => {
    store.dispatch({ type: "pageTitle/update", payload: t("common.screens") });
    store.dispatch({ type: "pageActions/update", payload: [] });
  }, [t]);

  return (
    <Grid container spacing={3}>
      {/* Cartes statistiques */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          {statsCards.map((stat, index) => (
            <Grid item xs={6} sm={4} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  background: stat.bgColor,
                  border: `1px solid ${alpha(stat.color, 0.2)}`,
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 4px 20px ${alpha(stat.color, 0.2)}`,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: alpha(stat.color, 0.15),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <stat.icon sx={{ color: stat.color, fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h4"
                      sx={{ fontWeight: 700, color: stat.color, lineHeight: 1 }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", mt: 0.5 }}
                    >
                      {stat.title}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Grid>

      {/* Filtre par établissement */}
      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            backgroundColor: alpha("#7a8a7a", 0.05),
            border: `1px solid ${alpha("#7a8a7a", 0.1)}`,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FilterList sx={{ color: "#7a8a7a", fontSize: 20 }} />
            <Typography variant="body2" sx={{ color: "#9aa89a", fontWeight: 500 }}>
              {t("common.filter_table")}
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel id="establishment-filter-label">
              {t("common.establishment")}
            </InputLabel>
            <Select
              labelId="establishment-filter-label"
              value={selectedEstablishment}
              label={t("common.establishment")}
              onChange={(e) => setSelectedEstablishment(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: alpha("#7a8a7a", 0.3),
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: alpha("#7a8a7a", 0.5),
                },
              }}
            >
              <MenuItem value="all">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Inventory2 sx={{ fontSize: 18, color: "#78a882" }} />
                  {t("common.all")} ({screens.length})
                </Box>
              </MenuItem>
              <MenuItem value="unassigned">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Tv sx={{ fontSize: 18, color: "#c4a574" }} />
                  {t("common.not_assigned")} (
                  {screens.filter((s) => !getScreenEstablishment(s)).length})
                </Box>
              </MenuItem>
              {establishments.map((estab) => {
                const count = screens.filter((s) => {
                  const e = getScreenEstablishment(s);
                  return e && parseInt(e.id) === parseInt(estab.id);
                }).length;
                return (
                  <MenuItem key={estab.id} value={estab.id}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Business sx={{ fontSize: 18, color: "#9a8878" }} />
                      {estab.name} ({count})
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {selectedEstablishment !== "all" && (
            <Typography
              variant="caption"
              sx={{
                color: "#9aa89a",
                ml: "auto",
              }}
            >
              {filteredScreens.length} {t("common.screens").toLowerCase()}
            </Typography>
          )}
        </Paper>
      </Grid>

      {/* Tableau des écrans */}
      <Grid item xs={12}>
        <ScreensTable filteredScreens={filteredScreens} />
      </Grid>
    </Grid>
  );
}

export default withAuthenticationRequired(Screens);
